import { api } from "./client";
import type {
  CollectionPackageItemSummary,
  CollectionPackageQueueItem,
} from "./collectionPackages";
import type { MaterialAudience } from "./portalMaterials";

// External Intake & Collection — Remediation Tier 2 #9 (Prompt 9-i).
// Admin registry over the typed external portal accounts. Mirrors
// PhoenixCapital.Core.DTOs.Portal.PortalAccountAdminDtos and the
// /api/portal/admin/accounts/* controller.

export type PortalUserKind = "Broker" | "PackageRecipient";

export interface PortalAccountListItemDto {
  id: string;
  email: string;
  fullName: string;
  kind: PortalUserKind;
  accountType: MaterialAudience;
  emailConfirmed: boolean;
  createdAt: string;
  lastLoginUtc: string | null;
  attachmentCount: number;
  // Account-First — per-account submission urgency, projected on the list
  // query so the registry table avoids an N+1 of summary fetches.
  overdueCount: number;
  awaitingReviewCount: number;
  totalPendingCount: number;
  lastActivityAt: string | null;
  /** Live assignment counts, separate from review-only urgency. */
  packageCount: number;
  totalItemCount: number;
  pendingRecipientActionCount: number;
  // Disable / Re-enable / Archive — registry status indicators.
  isDisabled?: boolean;
  disabledAtUtc?: string | null;
  isArchived?: boolean;
  archivedAtUtc?: string | null;
  /** Assignment Visibility (PROB-H) — staff assignees on this account.
   *  Projected onto the list response for the WS4 "Assigned staff" column +
   *  the assignedToUserId filter. Optional so legacy responses still parse. */
  assignedToUserIds?: string[];
}

// UAT FixBatch-06 FIND-31 — full-scope stat-card counts. Computed server-side
// over the filter+search-matched set (not just the loaded page).
export interface PortalAccountStatsDto {
  total: number;
  awaitingReview: number;
  overdue: number;
  noActivity7DPlus: number;
}

// UAT FixBatch-06 FIND-31 — paginated registry response.
export interface PortalAccountListResponse {
  items: PortalAccountListItemDto[];
  total: number;
  stats: PortalAccountStatsDto;
}

// Account-First — per-account External Collection urgency. overdueCount =
// AwaitingReview submissions whose parent package is past due and not
// Draft/Archived/Cancelled; awaitingReviewCount = AwaitingReview, not overdue,
// not Draft/Archived/Cancelled; totalPendingCount = overdue + awaiting;
// lastActivityAt = newest StagedSubmission.createdAt across the account.
export interface AccountSubmissionSummaryDto {
  overdueCount: number;
  awaitingReviewCount: number;
  totalPendingCount: number;
  lastActivityAt: string | null;
  /** Live assignment counts, separate from review-only urgency. */
  packageCount: number;
  totalItemCount: number;
  pendingRecipientActionCount: number;
}

export interface PortalAccountAttachmentDto {
  id: string;
  entityType: string;
  entityId: string;
  notes: string | null;
  createdAt: string;
}

export interface PortalAccountDetailDto {
  id: string;
  email: string;
  fullName: string;
  kind: PortalUserKind;
  accountType: MaterialAudience;
  emailConfirmed: boolean;
  createdAt: string;
  lastLoginUtc: string | null;
  linkedRecipientId: string | null;
  linkedBrokerId: string | null;
  attachments: PortalAccountAttachmentDto[];
  /** Assignment Visibility (PROB-H) — staff assignees on this account. */
  assignedToUserIds: string[];
  // Disable / Re-enable / Archive — current status + most recent disable
  // reason (cleared on re-enable). Drive the header chips + action buttons.
  isDisabled?: boolean;
  disabledAtUtc?: string | null;
  disabledReason?: string | null;
  isArchived?: boolean;
  archivedAtUtc?: string | null;
  // Vendor record foundations — Vendor ↔ portal-account cross-link
  // (PortalUser.LinkedVendorId), mirroring linkedBrokerId.
  linkedVendorId?: string | null;
  linkedVendorName?: string | null;
  // Portal-absorption §2.3 — additive credential/lifecycle facts (a sibling
  // backend workstream projects these; optional so legacy responses parse).
  lockoutEndUtc?: string | null;
  accessFailedCount?: number;
  mustChangePassword?: boolean;
  firstLoginAt?: string | null;
}

/** Disable / Re-enable / Archive — one row of the account Activity log,
 *  projected from AuditLog. `action` is the AuditAction as a string. */
export interface PortalAccountActivityDto {
  id: string;
  occurredAtUtc: string;
  actorDisplayName: string | null;
  actorRoleCode: string | null;
  action: string;
  description: string;
}

export interface AddPortalAccountAttachmentPayload {
  entityType: string;
  entityId: string;
  notes: string | null;
}

/** Assignment Visibility (PROB-H) — a resolved account assignee row. */
export interface PortalAccountAssigneeDto {
  id: string;
  userId: string;
  assignedAtUtc: string;
  assignedByUserId: string | null;
}

/** UAT F3 — staff edit of the account's display name / login email.
 *  Omitted (undefined) = unchanged; the server rejects a blank name or an
 *  invalid email (400) and a duplicate email (409). */
export interface UpdatePortalAccountIdentityPayload {
  fullName?: string;
  email?: string;
}

/** UAT F3 — pre-edit impact check: the account's in-flight e-signature
 *  envelopes (signer email is snapshotted at creation, so a login-email
 *  change does not follow envelopes already sent). Advisory, never a block. */
export interface PortalAccountIdentityImpactDto {
  activeSignatureEnvelopeCount: number;
}

/** WS2 — result of re-issuing the account's password link. `mode`
 *  distinguishes a set-password invite (unactivated account) from a reset
 *  link (activated account); `delivered` is false when nothing could be
 *  emailed, with `message` explaining the outcome either way. */
export interface ResendPasswordLinkResult {
  mode: "set-password" | "reset";
  delivered: boolean;
  message: string;
}

/** WS2 — a freshly minted temporary password. Surfaced to staff exactly
 *  once (never emailed); the user must change it at next sign-in. */
export interface TemporaryPasswordResult {
  temporaryPassword: string;
  mustChangeOnNextLogin: boolean;
}

// Remediation Tier 2 #9 (Prompt 9-ii) — typed signup-link DTOs. Staff
// pick a party type + one or more assignment groups (bundles); for a
// new account the backend mints + emails a magic-link, for a standing
// account the bundles are just pushed onto the existing dashboard.
export interface SendSignupLinkPayload {
  email: string;
  fullName: string;
  accountType: MaterialAudience;
  bundleIds: string[];
  ownerEntityType?: string | null;
  ownerEntityId?: string | null;
  recipientRoleLabel?: string | null;
  /** Assignment Visibility (PROB-I) — teammates the sender added via the
   *  "Also assign to" picker. The sending staff member is auto-assigned by
   *  the backend regardless of this list. */
  additionalAssigneeUserIds?: string[];
}

export interface IssueInvitationResult {
  invitationId: string;
  token: string;
  expiresAtUtc: string;
}

export interface SendSignupLinkResult {
  portalUserId: string;
  recipientId: string;
  packageIds: string[];
  isExistingAccount: boolean;
  invitation: IssueInvitationResult | null;
  /** Assignment Visibility (PROB-I) — every staff member now assigned to the
   *  account (sender + picked teammates). */
  assigneeUserIds: string[];
}

// Send Signup Link email typeahead — one unified contact search across the
// five staff sources. Mirrors PortalContactSearchResult on the backend
// (GET /portal/admin/accounts/contact-search?q=&limit=).
export type PortalContactSourceType =
  | "Person"
  | "LegalEntity"
  | "Broker"
  | "Vendor"
  | "ExternalRecipient";

export interface PortalContactSourceDto {
  sourceType: PortalContactSourceType;
  sourceId: string;
}

export interface PortalContactAccountDto {
  id: string;
  accountType: string;
  isActive: boolean;
}

export interface PortalContactSearchResultDto {
  displayName: string;
  email: string;
  sources: PortalContactSourceDto[];
  suggestedAccountType: "Broker" | "Vendor" | null;
  portalAccount: PortalContactAccountDto | null;
}

/** WS-F — the account-scoped per-package items read, wrapping the item set
 *  with a package-level `canDeletePackage` flag (every item — incl. archived —
 *  is untouched, the same eligibility the server enforces on package delete).
 *  Mirrors PhoenixCapital.Core.DTOs.Collection.AccountPackageItemsDto. */
export interface AccountPackageItems {
  canDeletePackage: boolean;
  items: CollectionPackageItemSummary[];
}

// Status filter values surfaced by the registry chip group (WS3/WS4). When
// `status` is set the server-side filter subsumes the legacy `includeArchived`
// boolean (status="archived" is one of the chip values), so callers must NOT
// also send `includeArchived` when status is non-empty.
export type PortalAccountStatusFilter =
  | "awaiting"
  | "active"
  | "disabled"
  | "archived";

// Sortable column ids whitelisted by the WS3 server. Each maps to a single
// projected list-DTO column; "createdAt" is the legacy default.
export type PortalAccountSortBy =
  | "createdAt"
  | "lastLoginUtc"
  | "lastActivityAt"
  | "overdueCount"
  | "awaitingReviewCount";

export type PortalAccountSortDir = "asc" | "desc";

// Date-window column toggle for the from/to range filter.
export type PortalAccountDateField =
  | "createdAt"
  | "lastLoginUtc"
  | "lastActivityAt";

export interface PortalAccountListParams {
  accountType?: MaterialAudience;
  search?: string;
  offset?: number;
  limit?: number;
  /** Multi-value status chips. Repeated as `?status=active&status=disabled`. */
  status?: PortalAccountStatusFilter[];
  /** Assigned-staff filter — single GUID. */
  assignedToUserId?: string;
  sortBy?: PortalAccountSortBy;
  sortDir?: PortalAccountSortDir;
  dateField?: PortalAccountDateField;
  /** ISO-8601 lower bound (inclusive). */
  from?: string;
  /** ISO-8601 upper bound (inclusive). */
  to?: string;
  /** KPI quick-filter: only rows with awaitingReviewCount > 0. */
  awaitingReviewOnly?: boolean;
  /** KPI quick-filter: only rows with overdueCount > 0. */
  overdueOnly?: boolean;
  /** KPI quick-filter: only rows whose lastActivityAt is N+ days stale. */
  noActivityDays?: number;
  /** Legacy archived toggle. Subsumed by `status` when set. */
  includeArchived?: boolean;
}

type ListQuery = Record<string, string | string[]>;

export const portalAccountsApi = {
  // UAT FixBatch-06 FIND-31 — paginated. Returns { items, total, stats }; the
  // stats reflect the full matched scope regardless of offset/limit.
  list: (params: PortalAccountListParams = {}) => {
    const qs: ListQuery = {};
    if (params.accountType) qs.accountType = params.accountType;
    if (params.search) qs.search = params.search;
    if (params.offset !== undefined) qs.offset = String(params.offset);
    if (params.limit !== undefined) qs.limit = String(params.limit);
    if (params.status && params.status.length > 0) {
      // Repeated `?status=` params — ASP.NET model-binds the list shape.
      qs.status = params.status;
    } else if (params.includeArchived) {
      // Only honor the legacy toggle when the new status chips aren't set;
      // status="archived" subsumes it.
      qs.includeArchived = "true";
    }
    if (params.assignedToUserId) qs.assignedToUserId = params.assignedToUserId;
    if (params.sortBy) qs.sortBy = params.sortBy;
    if (params.sortDir) qs.sortDir = params.sortDir;
    if (params.dateField) qs.dateField = params.dateField;
    if (params.from) qs.from = params.from;
    if (params.to) qs.to = params.to;
    if (params.awaitingReviewOnly) qs.awaitingReviewOnly = "true";
    if (params.overdueOnly) qs.overdueOnly = "true";
    if (params.noActivityDays !== undefined && params.noActivityDays > 0) {
      qs.noActivityDays = String(params.noActivityDays);
    }
    return api.get<PortalAccountListResponse>("/portal/admin/accounts", qs);
  },
  get: (id: string) =>
    api.get<PortalAccountDetailDto>(`/portal/admin/accounts/${id}`),
  /** UAT F3 — edit display name / login email. Returns the refreshed detail. */
  updateIdentity: (id: string, body: UpdatePortalAccountIdentityPayload) =>
    api.patch<PortalAccountDetailDto>(`/portal/admin/accounts/${id}`, body),
  /** UAT F3 — advisory in-flight e-signature envelope count for the edit dialog. */
  identityImpact: (id: string) =>
    api.get<PortalAccountIdentityImpactDto>(
      `/portal/admin/accounts/${id}/identity-impact`,
    ),
  /** WS2 — re-issue the account's password link (a set-password invite for an
   *  unactivated account, else a password-reset link). */
  resendPasswordLink: (id: string) =>
    api.post<ResendPasswordLinkResult>(
      `/portal/admin/accounts/${id}/resend-password-link`,
    ),
  /** WS2 — mint a one-time temporary password (shown to staff once, never
   *  emailed; the user must change it at next sign-in). */
  setTemporaryPassword: (id: string) =>
    api.post<TemporaryPasswordResult>(
      `/portal/admin/accounts/${id}/temp-password`,
    ),
  /** Account-First — per-account submission urgency for the detail header. */
  submissionSummary: (id: string) =>
    api.get<AccountSubmissionSummaryDto>(
      `/portal/admin/accounts/${id}/submission-summary`,
    ),
  attach: (id: string, body: AddPortalAccountAttachmentPayload) =>
    api.post<PortalAccountAttachmentDto>(
      `/portal/admin/accounts/${id}/attachments`,
      body,
    ),
  detach: (id: string, attachmentId: string) =>
    api.delete<null>(`/portal/admin/accounts/${id}/attachments/${attachmentId}`),
  /** Assignment Visibility (PROB-H) — staff assignees on an account. */
  listAssignees: (id: string) =>
    api.get<PortalAccountAssigneeDto[]>(`/portal/admin/accounts/${id}/assignees`),
  addAssignee: (id: string, userId: string) =>
    api.post<PortalAccountAssigneeDto>(`/portal/admin/accounts/${id}/assignees`, {
      userId,
    }),
  removeAssignee: (id: string, userId: string) =>
    api.delete<null>(`/portal/admin/accounts/${id}/assignees/${userId}`),
  sendSignupLink: (body: SendSignupLinkPayload) =>
    api.post<SendSignupLinkResult>("/portal/admin/accounts/signup-link", body),
  /** Send Signup Link typeahead — unified contact search across the five
   *  staff sources (Person/LegalEntity/Broker/Vendor/ExternalRecipient).
   *  Backend returns [] for trimmed q under 2 chars. */
  searchContacts: (q: string, limit?: number) => {
    const qs: Record<string, string> = { q };
    if (limit !== undefined) qs.limit = String(limit);
    return api.get<PortalContactSearchResultDto[]>(
      "/portal/admin/accounts/contact-search",
      qs,
    );
  },
  /** Account-First Phase 1 — packages on the account hub. Defaults to
   *  outstanding only (matches FL-9 exclusion of Completed + Cancelled);
   *  pass `includeAll=true` for the "show all" toggle. */
  listPackages: (id: string, includeAll = false) =>
    api.get<CollectionPackageQueueItem[]>(
      `/portal/admin/accounts/${id}/packages`,
      includeAll ? { includeAll: "true" } : {},
    ),
  /** F8 — the item list for one of this account's packages (name / kind /
   *  status + WS-F archive/delete gating), for the party-surface "request view"
   *  slide-out. Gated by Portal.ManageAccounts + IDOR-scoped to this account
   *  server-side. `includeArchived` un-hides archived items so the drawer's
   *  "Show archived" toggle + Unarchive work. */
  listPackageItems: (id: string, packageId: string, includeArchived = false) =>
    api.get<AccountPackageItems>(
      `/portal/admin/accounts/${id}/packages/${packageId}/items`,
      { includeArchived },
    ),

  // Disable / Re-enable / Archive — admin status actions. Disable/enable
  // require a non-blank reason (the modal enforces a 10-char minimum; the
  // server enforces non-blank); archive/unarchive accept an optional reason.
  disable: (id: string, reason: string) =>
    api.post<null>(`/portal/admin/accounts/${id}/disable`, { reason }),
  enable: (id: string, reason: string) =>
    api.post<null>(`/portal/admin/accounts/${id}/enable`, { reason }),
  archive: (id: string, reason?: string) =>
    api.post<null>(`/portal/admin/accounts/${id}/archive`, { reason: reason ?? null }),
  unarchive: (id: string, reason?: string) =>
    api.post<null>(`/portal/admin/accounts/${id}/unarchive`, { reason: reason ?? null }),
  getActivity: (id: string) =>
    api.get<PortalAccountActivityDto[]>(`/portal/admin/accounts/${id}/activity`),

  // Portal-absorption §2.3 — credential/session admin actions. Body-less
  // POSTs, mirroring resendPasswordLink / setTemporaryPassword above.
  unlock: (id: string) =>
    api.post<null>(`/portal/admin/accounts/${id}/unlock`),
  forcePasswordChange: (id: string) =>
    api.post<null>(`/portal/admin/accounts/${id}/force-password-change`),
  signOutEverywhere: (id: string) =>
    api.post<null>(`/portal/admin/accounts/${id}/sign-out-everywhere`),
};
